import React, { FC, memo, useEffect } from "react";
import { useSelector } from "react-redux";
import { createUseStyles } from "react-jss";

import { areEqual, areEqualShallow } from "Utils/areEqual";
import LocationEnd from "Assets/images/location_end.svg";
import LocationOther from "Assets/images/location_other.svg";
import OverlayView from "Components/PopUpOverlay";
import PopUpOverLay from "Containers/PopUpOverLay";
import styles from "./styles";
import PopUpContent from "./PopUpContent";

const createOverlayElement = (stopName) => {
  // Wrapper element
  const container = document.createElement("div"); // div.container
  container.setAttribute("data-trail-stop", `${stopName}`);
  container.style.position = "absolute";
  container.style.zIndex = "1000";
  return container;
};

// Create overlays (used in QA testing)
const useOverlaysForQaTesting = (mapObj: any, pathStops: any[]) => {
  useEffect(() => {
    let overlays = [];
    if (mapObj) {
      overlays = pathStops.map((pathStop) => {
        const overlay = new OverlayView({
          position: new window.google.maps.LatLng(
            pathStop.realLat,
            pathStop.realLng
          ),
          content: createOverlayElement(pathStop.title),
        });

        overlay.setMap(mapObj.map);
        return overlay;
      });
    }

    return () => {
      overlays.forEach((overlay: any) => {
        overlay.setMap(null);
      });
    };
  }, [mapObj, pathStops]);
};

// Get directions and draw path
const useDirectionsPath = (mapObj: any, pathStops: any[]) => {
  useEffect(() => {
    if (mapObj) {
      const pathStopsLatLng = pathStops
        .map((pathStop) => {
          if (pathStop) {
            return new window.google.maps.LatLng(
              Number(pathStop.realLat),
              Number(pathStop.realLng)
            );
          }
          return undefined;
        })
        .filter((pathStop) => pathStop !== undefined);

      // Google allows maximum 25 stopovers (waypoints) per API call.
      // Actual max limit would be 27 if origin and destination are included, but keep it as 25.
      const MAX_STOPS_PER_API_CALL = 25; // starting point (origin) is counted as 1 stop
      const pathSplitIncrement = MAX_STOPS_PER_API_CALL - 1; // subtract 1 for starting point
      const lastStopIndex = pathStops.length - 1;
      for (let i = 0; i < lastStopIndex; i += pathSplitIncrement) {
        const startIndex = i;
        const destIndex = Math.min(lastStopIndex, i + pathSplitIncrement);

        const startPoint = pathStopsLatLng[startIndex];
        const destPoint = pathStopsLatLng[destIndex];
        const stopovers = pathStopsLatLng.slice(startIndex + 1, destIndex);

        if (startPoint && destPoint) {
          mapObj.getDirections(
            startPoint,
            stopovers,
            destPoint,
            (success, msg) => {
              // eslint-disable-next-line no-console
              console.log("getDirections", success, msg);
            }
          );
        }
      }
    }
  }, [mapObj, pathStops]);
};

interface Props {
  mapObj: any;
}

const DirectionsAndPopUps: FC<Props> = memo(({ mapObj }: Props) => {
  const useStyles = createUseStyles(styles);
  const classes = useStyles();

  const pathStops: any[] = useSelector(
    (selectors: any) => selectors.pathStops.pathStops,
    areEqual
  );
  pathStops.sort((a, b) => Number(a.sort_order) - Number(b.sort_order));

  if (mapObj) {
    mapObj.clearCurrentDirections();
  }

  useOverlaysForQaTesting(mapObj, pathStops);
  useDirectionsPath(mapObj, pathStops);

  return (
    <>
      {pathStops.map((stop, index) => {
        const isLastStop = index === pathStops.length - 1;
        return (
          <PopUpOverLay
            key={stop.title}
            mapObj={mapObj}
            position={
              mapObj.isCustomTiledMap
                ? new window.google.maps.LatLng(stop.customLat, stop.customLng)
                : new window.google.maps.LatLng(stop.realLat, stop.realLng)
            }
          >
            <PopUpContent
              classes={classes}
              icon={isLastStop ? <LocationEnd /> : <LocationOther />}
              title={stop.title}
            />
          </PopUpOverLay>
        );
      })}
    </>
  );
}, areEqualShallow);
export default DirectionsAndPopUps;
